import { pipe } from 'fp-ts/function';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { z } from 'zod';
import {
    DateService,
    DateTimeService,
    DateTimeUtcService,
    DateTimeUtils,
    ISOFormat,
    TimeService,
} from '@mews-framework/datetime';
import { useTimezone } from '@mews-framework/globalization';
import { TimeService as TimeServiceType } from '@optimus-web/core';
import { FormDatePickerFieldProps, FormTimePickerProps } from '../fields';
import { timeIsoStringSchema, Validation } from '../validation';

const useDefaultValues = (fieldName: string, timeService: TimeServiceType) => {
    const { getValues } = useFormContext();
    const timezone = useTimezone();

    const utcValue = getValues(fieldName);
    const isValidUtc = DateTimeUtcService.isValidString(utcValue);

    return {
        date: isValidUtc ? DateTimeUtils.dateTimeUtcStringToDateISOString(utcValue, timezone) : null,
        time: isValidUtc
            ? pipe(
                  DateTimeUtils.dateTimeUtcStringToTime(utcValue, timezone),
                  DateTimeUtils.timeToStructuredTime,
                  timeService.getIsoString
              )
            : null,
    };
};

const useGetUtcValue = ({ dateFieldName, timeFieldName }: { dateFieldName: string; timeFieldName: string }) => {
    const { getValues } = useFormContext();
    const timezone = useTimezone();

    return () => {
        const [date, time] = getValues([dateFieldName, timeFieldName]);
        const parsedDate =
            date && DateTimeUtils.isValidDateIsoString(date)
                ? DateService.fromString(date, ISOFormat.Date, timezone)
                : null;
        const parsedTime = time && TimeService.isValidTimeString(time) ? TimeService.parse(time) : null;

        if (parsedDate != null && parsedTime != null) {
            const dateTime = DateTimeService.fromDateAndTime(parsedDate, parsedTime);
            return DateTimeService.toUtc(dateTime).value;
        }
        return null;
    };
};

const useResetTimeField = (timeFieldName: string) => {
    const { getValues, setValue, trigger } = useFormContext();
    const ZERO_TIME = '00:00:00';

    return () => {
        if (getValues(timeFieldName) == null) {
            setValue(timeFieldName, ZERO_TIME);
            void trigger(timeFieldName);
        }
    };
};

export function createDateTimeFieldNames<T extends string>(name: T) {
    return {
        date: `${name}Raw.date` as const,
        time: `${name}Raw.time` as const,
    };
}

interface DateTimeFieldHookParams {
    name: string;
    onChange?: (value?: string | null) => void;
    dateProps?: Pick<FormDatePickerFieldProps, 'onDateChange' | 'onChange'>;
    timeProps?: Pick<FormTimePickerProps, 'onChange'>;
    timeService: TimeServiceType;
}
export const useDateTimeCompoundField = ({
    name,
    onChange,
    dateProps,
    timeProps,
    timeService,
}: DateTimeFieldHookParams) => {
    const { date: dateFieldName, time: timeFieldName } = createDateTimeFieldNames(name);

    const defaultValues = useDefaultValues(name, timeService);
    const getUtcValue = useGetUtcValue({ dateFieldName, timeFieldName });
    const { setValue, watch } = useFormContext();
    const resetTimeField = useResetTimeField(timeFieldName);

    const handleParsedDateChange: FormDatePickerFieldProps['onDateChange'] = (date) => {
        resetTimeField();
        const utcValue = getUtcValue();
        setValue(name, utcValue);
        dateProps?.onDateChange?.(date);
    };

    const handleDateChange: FormDatePickerFieldProps['onChange'] = (event, date) => {
        const utcValue = getUtcValue();
        setValue(name, utcValue);
        onChange?.(utcValue);
        dateProps?.onChange?.(event, date);
    };

    const handleTimeChange: FormTimePickerProps['onChange'] = (time) => {
        const utcValue = getUtcValue();
        setValue(name, utcValue);
        onChange?.(utcValue);
        timeProps?.onChange?.(time);
    };

    const value = watch(name);

    // to set internal fields values when external value changed (setting field via setValue, reset form)
    useEffect(() => {
        const utcValue = getUtcValue();
        if (value !== utcValue) {
            setValue(dateFieldName, defaultValues.date);
            setValue(timeFieldName, defaultValues.time);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);
    return { dateFieldName, timeFieldName, defaultValues, handleParsedDateChange, handleDateChange, handleTimeChange };
};

/**
 * @description schema used for validation of compound DateTimeField (both parts are validated separately)
 * @example
 * z.object({ date: dateTimeUtcStringSchema }).extend<{}>(
 *   createDateTimeSchema({ fieldName: 'date', required: true })
 * )
 */
export const createDateTimeSchema = ({ fieldName, required }: { fieldName: string; required?: boolean }) => {
    const { date, time } = createDateTimeFieldNames(fieldName);
    const [prefix, dateField] = date.split('.');
    const [, timeField] = time.split('.');

    return {
        [prefix]: z
            .object({
                [dateField]: required ? Validation.Date.Iso : z.nullable(Validation.Date.Iso),
                [timeField]: required ? timeIsoStringSchema : z.nullable(timeIsoStringSchema),
            })
            .optional(),
    };
};
